/**
 * Angebot · Einblicke (Migration 730) — die Gastseite meldet Verweildauer und Lesefortschritt.
 *
 * Rechtlicher Rahmen (verbindlich): nur Daten, die der Gast ueber SEINEN Link selbst ausloest,
 * per normalem Request an unsere eigene API. Es wird NICHTS auf dem Geraet gespeichert oder
 * ausgelesen: kein Cookie, kein localStorage/sessionStorage/IndexedDB, kein Fingerprint, keine
 * Drittanbieter-Skripte. Die Kennung des Aufrufs (`aufrufId`) lebt nur im Arbeitsspeicher der
 * Seite und endet mit ihr. Gemessen wird nur, was die Seite selbst zeigt: wie weit gescrollt
 * wurde (Prozent), wie lange sie offen war (Sekunden) und ob der Preis in den sichtbaren
 * Bereich kam.
 *
 * Nie in der Betreiber-Vorschau (`vorschau`), nie ohne Aufruf-Kennung.
 */
import { useEffect } from "react";
import type { AngebotsEreignisArt } from "./angebots-ereignis";

/** Hoechstens so viele Sekunden werden gemeldet (Server klemmt ebenfalls auf 0..86400). */
export const MAX_DAUER_SEKUNDEN = 86_400;

/** Scroll-Fortschritt in Prozent (0..100): unterste sichtbare Pixelzeile / Seitenhoehe. */
export function scrollProzent(scrollY: number, viewportHoehe: number, seitenHoehe: number): number {
  if (!(seitenHoehe > 0)) return 0;
  const p = ((scrollY + viewportHoehe) / seitenHoehe) * 100;
  if (!Number.isFinite(p)) return 0;
  return Math.max(0, Math.min(100, Math.round(p)));
}

export interface AbschlussMeldung {
  aufrufId: string;
  dauerSekunden: number;
  scrollProzent: number;
}

/**
 * Schickt die Abschlussmeldung. `sendBeacon` ueberlebt das Schliessen des Tabs; ohne ihn faellt
 * es auf `fetch` mit `keepalive` zurueck. Jeder Fehler wird verschluckt - die Messung darf die
 * Seite nie stoeren.
 */
export function sendeAbschluss(token: string, m: AbschlussMeldung): void {
  const url = `/api/public/offer/${encodeURIComponent(token)}/aufruf-abschluss`;
  const body = JSON.stringify(m);
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        // text/plain ist CORS-safelisted (kein Preflight); der Server liest den Body ohnehin als Text.
        if (navigator.sendBeacon(url, new Blob([body], { type: "text/plain;charset=UTF-8" }))) return;
      } catch {
        // Ausnahme wie false behandeln: weiter mit fetch
      }
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => undefined);
  } catch {
    // bewusst still
  }
}

/**
 * Haengt die Messung an die Seite:
 *  - Scroll-Maximum in Prozent (passiver Listener),
 *  - Abschlussmeldung bei `visibilitychange` -> hidden und bei `pagehide`,
 *  - `preis_gesehen`, sobald ein Element mit `data-einblick-preis` im sichtbaren Bereich ist.
 *
 * `aufrufId()` wird erst beim Senden gelesen (die Kennung kommt mit der Antwort auf den Abruf).
 */
export function useGastEinblicke(args: {
  token: string | undefined;
  vorschau: boolean;
  /** Die Seite ist geladen und zeigt das Angebot (Preis im DOM). */
  bereit: boolean;
  aufrufId: () => string | null;
  melde: (art: AngebotsEreignisArt) => void;
}): void {
  const { token, vorschau, bereit, aufrufId, melde } = args;

  useEffect(() => {
    if (vorschau || !token || typeof window === "undefined") return;
    const start = Date.now();
    let maxScroll = 0;
    let zuletztGesendet = "";

    const messen = () => {
      const doc = document.documentElement;
      const p = scrollProzent(window.scrollY || doc.scrollTop || 0, window.innerHeight || doc.clientHeight || 0, doc.scrollHeight || 0);
      if (p > maxScroll) maxScroll = p;
    };
    const senden = () => {
      const id = aufrufId();
      if (!id) return;
      messen();
      const dauer = Math.min(MAX_DAUER_SEKUNDEN, Math.max(0, Math.round((Date.now() - start) / 1000)));
      const schluessel = `${id}:${dauer}:${maxScroll}`;
      if (schluessel === zuletztGesendet) return;
      zuletztGesendet = schluessel;
      sendeAbschluss(token, { aufrufId: id, dauerSekunden: dauer, scrollProzent: maxScroll });
    };
    const beiSichtbarkeit = () => {
      if (document.visibilityState === "hidden") senden();
    };

    window.addEventListener("scroll", messen, { passive: true });
    window.addEventListener("resize", messen, { passive: true });
    document.addEventListener("visibilitychange", beiSichtbarkeit);
    window.addEventListener("pagehide", senden);
    messen();
    return () => {
      window.removeEventListener("scroll", messen);
      window.removeEventListener("resize", messen);
      document.removeEventListener("visibilitychange", beiSichtbarkeit);
      window.removeEventListener("pagehide", senden);
    };
    // aufrufId/melde sind stabile Leser (Ref bzw. Funktion), kein Grund zum Neuaufbau.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, vorschau]);

  useEffect(() => {
    if (vorschau || !token || !bereit || typeof document === "undefined") return;
    if (typeof IntersectionObserver === "undefined") return;
    const ziele = Array.from(document.querySelectorAll("[data-einblick-preis]"));
    if (ziele.length === 0) return;
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) {
          melde("preis_gesehen");
          beobachter.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    for (const z of ziele) beobachter.observe(z);
    return () => beobachter.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, vorschau, bereit]);
}
